import * as React from 'react'
import { useQuery } from '@apollo/react-hooks'
import { NetworkStatus } from 'apollo-client'
import { isNil, pickBy } from 'lodash'

import { Meta } from '~/components'
import { catalogSearchQuery } from '~/gql/queries'
import { useFiltersState } from '~/hooks/use-filters-state'
import { MainLayout } from '~/layouts'
import { notifySentry, queryUpdater } from '~/utils'
import type { GetMetaInfoResult } from '~/utils/get-meta-info'

import { Content } from './components/content'
import { Header } from './components/header/header'
import type { IQueryData, IQueryVariables } from './types'

interface SearchBetaScreenProps {
  metaInfo: GetMetaInfoResult
  isCategoryPage?: boolean
  page?: number
  categoryName?: string
  subcategoryName?: string
}

function SearchBetaScreen(props: SearchBetaScreenProps) {
  const { metaInfo, isCategoryPage, subcategoryName, categoryName, page } = props

  const { fixedSerializedFilters, serializedFilters, term, setTotalOfResults, sortBy } = useFiltersState()

  const queryVariables = React.useMemo(() => {
    return pickBy(
      { term, page, filters: [...fixedSerializedFilters, ...serializedFilters], sortBy },
      item => !isNil(item),
    )
  }, [fixedSerializedFilters, page, serializedFilters, term, sortBy])

  const catalogSearchQueryResponse = useQuery<IQueryData, IQueryVariables>(catalogSearchQuery, {
    variables: queryVariables,
    onError: apolloError => notifySentry(apolloError),
  })

  const paginationInfo = catalogSearchQueryResponse.data?.catalogSearch?.pageInfo ?? ({} as IPagination)

  const products = catalogSearchQueryResponse.data?.catalogSearch?.items ?? []

  const headerTitle = subcategoryName || categoryName || `"${term}"`

  const onFetchMoreProducts = React.useCallback(
    async (page: number) => {
      await catalogSearchQueryResponse.fetchMore({
        updateQuery: queryUpdater,
        variables: {
          ...queryVariables,
          page,
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryVariables],
  )

  React.useEffect(() => {
    if (!isNil(paginationInfo.total)) {
      setTotalOfResults(paginationInfo.total)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationInfo.total])

  return (
    <>
      <Meta
        title={metaInfo.title}
        image={metaInfo.image}
        url={metaInfo.url}
        description={metaInfo.description}
        asCanonical={isCategoryPage}
        prevPage={paginationInfo.hasPrevPage ? String(paginationInfo.page - 1) : null}
        nextPage={paginationInfo.hasNextPage ? String(paginationInfo.page + 1) : null}
      />
      <MainLayout>
        <Header title={headerTitle} numOfHints={paginationInfo.total} />
        <Content
          products={products}
          networkStatus={
            catalogSearchQueryResponse.loading ? NetworkStatus.loading : catalogSearchQueryResponse.networkStatus
          }
          paginationInfo={paginationInfo}
          onFetchMoreProducts={onFetchMoreProducts}
        />
      </MainLayout>
    </>
  )
}

export default SearchBetaScreen
